import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AppProvider, useApp } from "./context/AppContext";
import StoreLayout from "./components/store/StoreLayout";
import AdminLayout from "./components/admin/AdminLayout";
import AuthCallback from "./pages/store/AuthCallback";
import Home from "./pages/store/Home";
import Catalog from "./pages/store/Catalog";
import ProductPage from "./pages/store/ProductPage";
import CartPage from "./pages/store/CartPage";
import Checkout from "./pages/store/Checkout";
import { PaymentSuccess, PaymentCancel } from "./pages/store/Payment";
import { Login, Register, Forgot, Reset } from "./pages/store/AuthPages";
import Account from "./pages/store/Account";
import OrderDetail from "./pages/store/OrderDetail";
import Help from "./pages/store/Help";
import Legal from "./pages/store/Legal";
import BrandPage from "./pages/store/BrandPage";
import Dashboard from "./pages/admin/Dashboard";
import Pipeline from "./pages/admin/Pipeline";
import ProductAdmin from "./pages/admin/ProductAdmin";
import Pricing from "./pages/admin/Pricing";
import Suppliers from "./pages/admin/Suppliers";
import Orders from "./pages/admin/Orders";
import OrderAdmin from "./pages/admin/OrderAdmin";
import Refunds from "./pages/admin/Refunds";
import Campaigns from "./pages/admin/Campaigns";
import Agents from "./pages/admin/Agents";
import Controls from "./pages/admin/Controls";
import BrandsMarkets from "./pages/admin/BrandsMarkets";
import AuditPage from "./pages/admin/AuditPage";
import SiteSettings from "./pages/admin/SiteSettings";
import ReviewsAdmin from "./pages/admin/ReviewsAdmin";
import FacebookAdmin from "./pages/admin/FacebookAdmin";

const Loading = () => <div className="p-16 label-xs" data-testid="auth-loading">Vérification de la session…</div>;

function RequireAuth({ children, admin }) {
  const { user } = useApp();
  const loc = useLocation();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname)}`} replace />;
  if (admin && !["owner", "admin"].includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route element={<StoreLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/shop" element={<Catalog />} />
        <Route path="/product/:id" element={<ProductPage />} />
        <Route path="/brand/:slug" element={<BrandPage />} />
        <Route path="/cart" element={<CartPage />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<Forgot />} />
        <Route path="/reset-password" element={<Reset />} />
        <Route path="/account" element={<RequireAuth><Account /></RequireAuth>} />
        <Route path="/account/orders/:id" element={<RequireAuth><OrderDetail /></RequireAuth>} />
        <Route path="/help" element={<Help />} />
        <Route path="/legal/:slug" element={<Legal />} />
      </Route>
      <Route path="/admin" element={<RequireAuth admin><AdminLayout /></RequireAuth>}>
        <Route index element={<Dashboard />} />
        <Route path="pipeline" element={<Pipeline />} />
        <Route path="products/:id" element={<ProductAdmin />} />
        <Route path="pricing" element={<Pricing />} />
        <Route path="suppliers" element={<Suppliers />} />
        <Route path="orders" element={<Orders />} />
        <Route path="orders/:id" element={<OrderAdmin />} />
        <Route path="refunds" element={<Refunds />} />
        <Route path="campaigns" element={<Campaigns />} />
        <Route path="agents" element={<Agents />} />
        <Route path="controls" element={<Controls />} />
        <Route path="brands" element={<BrandsMarkets />} />
        <Route path="audit" element={<AuditPage />} />
        <Route path="site" element={<SiteSettings />} />
        <Route path="reviews" element={<ReviewsAdmin />} />
        <Route path="facebook" element={<FacebookAdmin />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App grain">
      <BrowserRouter>
        <AppProvider>
          <AppRouter />
          <Toaster theme="dark" position="top-center" />
        </AppProvider>
      </BrowserRouter>
    </div>
  );
}
